/**
 * KeyboardAwareFormScrollView - Web Implementation
 *
 * On web, browsers handle keyboard avoidance natively, so we just use a standard ScrollView.
 */

import React, { forwardRef, memo, ReactNode } from 'react';
import {
  ScrollView,
  ScrollViewProps,
  StyleProp,
  ViewStyle,
} from 'react-native';

/**
 * Default bottom offset for keyboard spacing (pixels).
 * Used by both CreateRequest and RequestDetail screens.
 */
export const KEYBOARD_BOTTOM_OFFSET = 100;

export interface KeyboardAwareFormScrollViewProps
  extends Omit<ScrollViewProps, 'ref'> {
  children: ReactNode;
  style?: StyleProp<ViewStyle>;
  contentContainerStyle?: StyleProp<ViewStyle>;
  /**
   * Extra space between the keyboard and focused input (pixels).
   * @default KEYBOARD_BOTTOM_OFFSET (100)
   */
  bottomOffset?: number;
  /**
   * Whether to show the vertical scroll indicator.
   * @default false
   */
  showsVerticalScrollIndicator?: boolean;
  /**
   * Test ID for testing purposes.
   */
  testID?: string;
  /**
   * Native-only. Ignored on web (browsers handle keyboard avoidance).
   */
  enabled?: boolean;
}

/**
 * Web implementation - uses standard ScrollView.
 * Browsers handle keyboard avoidance natively.
 */
export const KeyboardAwareFormScrollView = memo(
  forwardRef<ScrollView, KeyboardAwareFormScrollViewProps>(
    (
      {
        children,
        style,
        contentContainerStyle,
        showsVerticalScrollIndicator = false,
        testID,
        bottomOffset: _bottomOffset,
        enabled: _enabled,
        ...rest
      },
      ref,
    ) => {
      return (
        <ScrollView
          ref={ref}
          style={style}
          contentContainerStyle={contentContainerStyle}
          showsVerticalScrollIndicator={showsVerticalScrollIndicator}
          keyboardShouldPersistTaps="handled"
          testID={testID}
          {...rest}
        >
          {children}
        </ScrollView>
      );
    },
  ),
);

KeyboardAwareFormScrollView.displayName = 'KeyboardAwareFormScrollView';

export default KeyboardAwareFormScrollView;
